import {
  type AiModelReference,
  type AiModelReferenceEntry,
  type AssetItem,
  type ParsedAssets,
} from '~/types/assets';

import {
  PARTIES,
  PARTY_NAMES,
  REPARSING_DATA_PATHS_MAP,
} from '~/modules/asset-reparsing/constants';
import {
  type ParserFormat,
  type PartyType,
  type ReparsingProblemType,
  type ReparsingTaskType,
} from '~/modules/asset-reparsing/types';
import {
  guaranteeDeliveryNoteLineItemStructure,
  guaranteeDeliveryNoteStructure,
} from '~/modules/delivery-notes';

import { getNestedProperty, isPlainObject } from '@vestigas/utils';
import {
  type DateLike,
  datetime4datetimeInput,
  formatFromUTCDate,
  i18n,
} from '~/utils';

export const executionTimestampFields = [
  'arrived',
  'arrivedPlanned',
  'beginExecution',
  'beginExecutionPlanned',
  'beginLoading',
  'beginLoadingPlanned',
  'beginUnloading',
  'beginUnloadingPlanned',
  'departure',
  'departurePlanned',
  'endExecution',
  'endExecutionPlanned',
  'endLoading',
  'endLoadingPlanned',
  'endUnloading',
  'endUnloadingPlanned',
] as const;

const problemTypeToTaskType: Record<ReparsingProblemType, ReparsingTaskType> = {
  correct_ai_parser_results: 'correctAIParserResults',
  missing_issuer: 'mapIssuer',
  missing_mapping: 'mapBuyer',
  missing_parties: 'mapCompany',
  unknown_unit: 'mapUnit',
};

type TaskContext =
  | Record<'reparsingParties', PartyType[]>
  | Record<'unit', string>
  | undefined;

type TaskDescriptionBuilder = (args: {
  problemDesc: string;
  reparsingParties: PartyType[];
}) => { taskContext?: TaskContext; taskDescription: string[] };

const taskDescriptionBuilders: Record<
  ReparsingProblemType,
  TaskDescriptionBuilder
> = {
  correct_ai_parser_results: () => ({
    taskDescription: [
      i18n.t(
        ($) =>
          $.assetReparsing.taskDescription.missingParties.description.intro,
      ),
      i18n.t(
        ($) =>
          $.assetReparsing.taskDescription.aiParserResults.description.problem,
      ),
      i18n.t(
        ($) =>
          $.assetReparsing.taskDescription.aiParserResults.description.task,
      ),
      i18n.t(
        ($) =>
          $.assetReparsing.taskDescription.missingParties.description.outro,
      ),
    ],
  }),

  missing_issuer: () => ({
    taskDescription: [
      i18n.t(
        ($) => $.assetReparsing.taskDescription.unknownIssuer.description.intro,
      ),
      i18n.t(
        ($) =>
          $.assetReparsing.taskDescription.unknownIssuer.description.problem,
      ),
      i18n.t(
        ($) => $.assetReparsing.taskDescription.unknownIssuer.description.task,
      ),
      i18n.t(
        ($) => $.assetReparsing.taskDescription.unknownIssuer.description.outro,
      ),
    ],
  }),

  missing_mapping: () => ({
    taskDescription: [
      i18n.t(
        ($) =>
          $.assetReparsing.taskDescription.missingMapping.description.intro,
      ),
      i18n.t(
        ($) =>
          $.assetReparsing.taskDescription.missingMapping.description.problem,
      ),
      i18n.t(
        ($) => $.assetReparsing.taskDescription.missingMapping.description.task,
      ),
      i18n.t(
        ($) =>
          $.assetReparsing.taskDescription.missingMapping.description.outro,
      ),
    ],
  }),

  missing_parties: ({ reparsingParties }) => ({
    taskContext: { reparsingParties },
    taskDescription: [
      i18n.t(
        ($) =>
          $.assetReparsing.taskDescription.missingParties.description.intro,
      ),
      i18n.t(
        ($) =>
          $.assetReparsing.taskDescription.missingParties.description.problem,
        {
          parties: reparsingParties
            .map((p) => PARTY_NAMES[p])
            .join(i18n.t(($) => $.joinList.and, { ns: 'common' })),
        },
      ),
      i18n.t(
        ($) => $.assetReparsing.taskDescription.missingParties.description.task,
      ),
      i18n.t(
        ($) =>
          $.assetReparsing.taskDescription.missingParties.description.outro,
      ),
    ],
  }),

  unknown_unit: ({ problemDesc }) => {
    // Extract the unit value (e.g., 'nicht Stück') from a problemDesc string like
    // "Unsupported UNCEFACT unit 'nicht Stück' in 'product_unit'"
    const unitMatch = /'([^']+)'/.exec(problemDesc);
    const unit = unitMatch?.[1]?.trim() ?? '';

    return {
      taskContext: { unit },
      taskDescription: [
        i18n.t(
          ($) => $.assetReparsing.taskDescription.unknownUnit.description.intro,
        ),
        i18n.t(
          ($) =>
            $.assetReparsing.taskDescription.unknownUnit.description.problem,
          { unit },
        ),
        i18n.t(
          ($) => $.assetReparsing.taskDescription.unknownUnit.description.task,
        ),
        i18n.t(
          ($) => $.assetReparsing.taskDescription.unknownUnit.description.outro,
        ),
      ],
    };
  },
};

/**
 * Retrieves type of task and useful instructions for user based on the given problem type and reparsing parties.
 *
 * @param {ReparsingProblemType} problemType - The type of problem encountered.
 * @param {PartyType[]} reparsingParties - The parties involved in the reparsing.
 * @return {{ taskDescription: string[]; taskType: ReparsingTaskType }} The task details including description and type.
 */
const getTaskDetails = ({
  problemDesc,
  problemType,
  reparsingParties,
}: {
  problemDesc: string;
  problemType: ReparsingProblemType;
  reparsingParties: PartyType[];
}) => {
  const taskType = problemTypeToTaskType[problemType] ?? 'notImplemented';
  const buildTaskDescription = taskDescriptionBuilders[problemType];

  if (!buildTaskDescription) {
    return { taskDescription: ['not implemented'], taskType };
  }

  const { taskContext, taskDescription } = buildTaskDescription({
    problemDesc,
    reparsingParties,
  });

  return { taskContext, taskDescription, taskType };
};

/**
 * Picks the properties we need from the raw reparsing asset object received from the API.
 * @param {RawReparsingAsset} reparsingAsset - The reparsing asset object to destructure.
 * @return {DestructuredReparsingAsset} The destructured reparsing asset properties.
 */
const destructRawReparsingAsset = (reparsingAsset: ParsedAssets) => {
  const reparsingData = {
    assetId: reparsingAsset?.id,
    problemType: reparsingAsset?.problemType,
    reparsingId: reparsingAsset?.reparsingId,
    reparsingParties: reparsingAsset?.parties ?? [],
  };

  const deliveryNote = guaranteeDeliveryNoteStructure(
    reparsingAsset.parsedAsset.body,
  );

  const lineItems = deliveryNote.transaction.logisticsPackage[0]?.lineItem; // logisticsPackage ALWAYS has only one item with all the data

  return {
    ...deliveryNote, // Return the full delivery note object
    assetParties: PARTIES.reduce<Record<PartyType, any>>(
      (accumulator, current) => {
        accumulator[current] = getNestedProperty(
          deliveryNote,
          REPARSING_DATA_PATHS_MAP[current],
        );

        return accumulator;
      },
      {},
    ),
    lineItems: lineItems?.map((lineItem) =>
      guaranteeDeliveryNoteLineItemStructure(lineItem),
    ),
    reparsingData, // Additional reparsing data to the delivery note
  };
};

const formatUtcTimestampsForDatetimeInput = (
  data: Record<string, DateLike>,
  timestampNames: string[],
) =>
  isPlainObject(data)
    ? Object.fromEntries(
        timestampNames.map((field) => {
          // Check if the field exists and has a value
          if (!field || !data[field]) {
            return [field, ''];
          }

          try {
            const formattedDate = formatFromUTCDate(
              data[field],
              datetime4datetimeInput,
            );

            return [field, formattedDate];
          } catch (error) {
            console.warn(
              `Failed to format date for field ${field}:`,
              data[field],
              error,
            );

            return [field, ''];
          }
        }),
      )
    : {};

function normalizeAiModels(
  modelReference: AiModelReference | undefined,
): AiModelReferenceEntry[] {
  if (!modelReference) return [];

  const references = Array.isArray(modelReference)
    ? modelReference
    : [modelReference];

  return references.map((reference) =>
    typeof reference === 'string'
      ? { modelId: reference, modelType: 'di' }
      : reference,
  );
}

export function mapAiProcessingDetails(
  handleDlnInput: AssetItem['handleDlnInput'] | undefined,
) {
  return {
    aiCustomSettings: handleDlnInput?.aiCustomSettings,
    aiModels: normalizeAiModels(handleDlnInput?.modelId),
    jobId: handleDlnInput?.jobId,
  };
}

type DestructuredReparsingAsset = ReturnType<typeof destructRawReparsingAsset>;

const mapDeliveryNoteExecutionTimestamps = (
  deliveryNote: Omit<
    DestructuredReparsingAsset,
    'assetParties' | 'lineItems' | 'reparsingData'
  >,
) => {
  const executionTimestamps = formatUtcTimestampsForDatetimeInput(
    deliveryNote.transaction.delivery.execution,
    executionTimestampFields,
  );

  return {
    ...deliveryNote,
    transaction: {
      ...deliveryNote.transaction,
      delivery: {
        ...deliveryNote.transaction.delivery,
        execution: {
          ...deliveryNote.transaction.delivery.execution,
          ...executionTimestamps,
        },
      },
    },
  };
};

const mapLineItemValidationTimestamps = (
  lineItems: DestructuredReparsingAsset['lineItems'],
) =>
  lineItems?.map((lineItem) => ({
    ...lineItem,
    product: {
      ...lineItem.product,
      concrete: {
        ...lineItem.product.concrete,
        lastValidation: formatFromUTCDate(
          lineItem.product.concrete.lastValidation,
          datetime4datetimeInput,
        ),
      },
    },
  })) ?? [];

/**
 * Creates the reparsing asset object we are using in the frontend based on the data from the API response.
 * @param {RawReparsingAsset} reparsingAssetRaw - The reparsing asset data to create the object from.
 * @return {Object} The created reparsing asset object.
 */
const createReparsingAsset = ({
  aiProcessingDetails,
  originalDlnFileName,
  parserFormat,
  reparsingAssetRaw,
}: {
  aiProcessingDetails: ReturnType<typeof mapAiProcessingDetails>;
  originalDlnFileName: string | undefined;
  parserFormat: ParserFormat;
  reparsingAssetRaw: ParsedAssets;
}) => {
  const { assetParties, lineItems, reparsingData, ...deliveryNote } =
    destructRawReparsingAsset(reparsingAssetRaw);

  const taskDetails = getTaskDetails({
    problemDesc: reparsingAssetRaw.problemDesc,
    problemType: reparsingData.problemType,
    reparsingParties: reparsingData.reparsingParties,
  });

  const mappedDeliveryNote = mapDeliveryNoteExecutionTimestamps(deliveryNote);
  const mappedLineItems = mapLineItemValidationTimestamps(lineItems);

  const reparsingAsset = {
    aiProcessingDetails,
    assetParties,
    assetRaw: reparsingAssetRaw,
    dataValidationErrors: reparsingAssetRaw.dataValidationErrors,
    dlnDate: formatFromUTCDate(
      deliveryNote?.header?.date,
      datetime4datetimeInput,
    ),
    dlnFileName: originalDlnFileName,
    lineItems: mappedLineItems,
    parserFormat,
    ...mappedDeliveryNote,
    ...reparsingData,
    ...taskDetails,
  };

  return reparsingAsset;
};

/**
 * Map an array of reparsing assets received from the backend to the data structure we use in the components.
 */
export const mapReparsingAssetsData = (reparsingAssets: AssetItem[] = []) =>
  reparsingAssets?.flatMap(
    ({ handleDlnInput, originalDlnFileName, parsedAssets }) =>
      parsedAssets
        ?.map((asset) => {
          const aiProcessingDetails = mapAiProcessingDetails(handleDlnInput);

          return createReparsingAsset({
            aiProcessingDetails,
            originalDlnFileName,
            parserFormat: (handleDlnInput?.parserFormat ??
              'ocr') as ParserFormat,
            reparsingAssetRaw: asset,
          });
        })
        .filter(Boolean) ?? [],
  );
